import { useHandleAnchor } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/anchor/useHandleAnchor';
import { useGetApPayeeInfo } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useGetApPayeeInfo';
import { DataMaster } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { Anchor, Loading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, Suspense, useRef } from 'react';
import {
  DefaultPaymentMethod,
  GeneralInformation,
  InvoiceReceivingMethod,
  PICAndContactInformation,
} from './components';
import { PayeeDetailDrawerProvider } from './context/PayeeDetailDrawerProvider';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);
type DetailDrawerContentProps = {
  handleUpdate?: () => void;
  data?: DataMaster | undefined;
  apPayeeId: string;
};

type Props = {
  apPayeeId: string;
  onClose?: () => void;
};

const DetailDrawerContent: FC<DetailDrawerContentProps> = ({
  data,
  apPayeeId,
}) => {
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const { handleAnchorClick, anchorItems, getContainer, getCurrentAnchor } =
    useHandleAnchor(contentWrapperRef);

  return (
    <div ref={contentWrapperRef} className={cx('detailDrawerBodyContainer')}>
      <Anchor
        getContainer={getContainer}
        getCurrentAnchor={getCurrentAnchor}
        items={anchorItems}
        affix={false}
        onClick={handleAnchorClick}
        className={cx('payeeAnchorContainer')}
        containerClassName={cx('anchorContainer')}
        contentClassName={cx('anchorContentContainer')}
      >
        <div className={cx('detailSectionContainer')}>
          <GeneralInformation data={data?.generalInformation} />
          <PICAndContactInformation data={data?.PICAndContractDetail} />
          <InvoiceReceivingMethod data={data?.receivingMethod} />
          <DefaultPaymentMethod
            apPayeeId={apPayeeId}
            paymentRule={data?.paymentRule}
            transactionDefaultValue={data?.transactionDefaultValue}
          />
        </div>
      </Anchor>
    </div>
  );
};

DetailDrawerContent.displayName = 'DetailDrawerContent';

const FallBackLoading = () => {
  return (
    <div className={cx('loadingContainer')}>
      <Loading size='large' color='success' />
    </div>
  );
};

const InnerDetailDrawerContainer: FC<Props> = ({ apPayeeId, onClose }) => {
  const { data, refetch } = useGetApPayeeInfo(apPayeeId);

  return (
    <PayeeDetailDrawerProvider refetch={refetch} onClose={onClose}>
      <DetailDrawerContent data={data} apPayeeId={apPayeeId} />
    </PayeeDetailDrawerProvider>
  );
};

InnerDetailDrawerContainer.displayName = 'InnerDetailDrawerContainer';

export const DetailDrawerContainer: FC<Props> = (props) => {
  return (
    <Suspense fallback={<FallBackLoading />}>
      <InnerDetailDrawerContainer {...props} />
    </Suspense>
  );
};

DetailDrawerContainer.displayName = 'DetailDrawerContainer';
